import React, { useState, useEffect, useRef } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import { motion } from 'framer-motion';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import ScaleIcon from '@mui/icons-material/Scale';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '@/context/AuthContext';
import { useCredits } from '@/context/CreditsContext';
import { useI18n } from '@/i18n/I18nContext';
import { PaymentService } from '@/services/PaymentService';
import type { SubscriptionResponse } from '@/services/PaymentService';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Slider from '@mui/material/Slider';

const PLAN_TO_PRODUCT_ID: Record<string, 'byok' | 'starter' | 'pro' | null> = {
	Free: null,
	'Bring Your Own Key': 'byok',
	Starter: 'starter',
	Pro: 'pro'
};

const PricingPage: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { user, getAuthHeader, getUserId } = useAuth();
	const { loadCredits } = useCredits();
	const { t } = useI18n();
	const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
	const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
	const [subscriptionLoading, setSubscriptionLoading] = useState(false);
	const [portalLoading, setPortalLoading] = useState(false);
	const [creditPackQuantity, setCreditPackQuantity] = useState(1);
	const confirmPaymentRan = useRef(false);

	const CREDITS_PER_PACK = 100;
	const PRICE_PER_PACK = 2; // $2 per 100 credits
	const MAX_PACKS = 20;

	// Load current subscription when user is logged in
	useEffect(() => {
		if (!user) {
			setSubscription(null);
			return;
		}
		const userId = getUserId();
		const authHeader = getAuthHeader() as Record<string, string> | undefined;
		if (!userId || !authHeader?.Authorization) {
			setSubscription(null);
			return;
		}
		setSubscriptionLoading(true);
		PaymentService.getSubscription(userId, authHeader)
			.then((res) => setSubscription(res))
			.catch(() => setSubscription(null))
			.finally(() => setSubscriptionLoading(false));
	}, [user, getUserId, getAuthHeader]);

	// On return from Stripe: success=1&session_id=... → confirm payment and add credits from product metadata
	useEffect(() => {
		const success = searchParams.get('success');
		const sessionId = searchParams.get('session_id');
		if (success !== '1' || !sessionId || confirmPaymentRan.current) return;

		const userId = getUserId();
		const authHeader = getAuthHeader() as Record<string, string> | undefined;
		if (!userId || !authHeader?.Authorization) {
			toast.error('Please sign in to complete your purchase.');
			setSearchParams({});
			return;
		}

		confirmPaymentRan.current = true;
		PaymentService.confirmPayment(userId, sessionId, authHeader)
			.then((res) => {
				if (res.success) {
					toast.success(
						res.credits > 0
							? `Payment successful! ${res.credits} credits have been added to your account.`
							: 'Payment successful!'
					);
					loadCredits();
				}
			})
			.catch((e: any) => {
				toast.error(e?.message || 'Could not confirm payment. Credits may still be added; check your balance.');
				loadCredits();
			})
			.finally(() => {
				setSearchParams({});
			});
	}, [searchParams, getUserId, getAuthHeader, loadCredits, setSearchParams]);

	const plans = [
		{
			name: 'Free',
			price: '$0',
			period: '',
			credits: '15 AI credits',
			description: 'Get started with no commitment',
			icon: <CardGiftcardIcon sx={{ fontSize: 40 }} />,
			color: 'info',
			features: [
				'15 AI credits to start',
				'Full access to all features',
				'No credit card required',
				'Upgrade anytime for more credits'
			],
			highlight: false,
			isFree: true,
			productId: null
		},
		{
			name: 'Bring Your Own Key',
			price: '$1',
			period: 'month',
			credits: 'On Demand',
			description: 'Use your own OpenAI API key',
			icon: <VpnKeyIcon sx={{ fontSize: 40 }} />,
			color: 'primary',
			features: [
				'Use your own OpenAI API key',
				'AI requests on demand',
				'Full access to all features',
				'You pay OpenAI directly'
			],
			highlight: false,
			isFree: false,
			productId: 'byok'
		},
		{
			name: 'Starter',
			price: '$3',
			period: 'month',
			credits: '500 AI credits',
			description: 'Perfect for casual brewers',
			icon: <StarIcon sx={{ fontSize: 40 }} />,
			color: 'secondary',
			features: [
				'500 AI credits per month',
				'Full access to all features',
				'AI-powered batch analysis',
				'Recipe recommendations'
			],
			highlight: true,
			isFree: false,
			productId: 'starter'
		},
		{
			name: 'Pro',
			price: '$5',
			period: 'month',
			credits: '1000 AI credits',
			description: 'For serious home brewers',
			icon: <StarIcon sx={{ fontSize: 40 }} />,
			color: 'success',
			features: [
				'1000 AI credits per month',
				'Full access to all features',
				'AI-powered batch analysis',
				'Recipe recommendations',
				'Priority support'
			],
			highlight: false,
			isFree: false,
			productId: 'pro'
		},
		{
			name: 'Buy Credits',
			price: '$2',
			period: 'per 100',
			credits: '100 AI credits',
			description: 'One-time credit purchase',
			icon: <CardGiftcardIcon sx={{ fontSize: 40 }} />,
			color: 'warning',
			features: [
				'100 AI credits per purchase',
				'Buy multiple packs (up to 20)',
				'Credits never expire',
				'Perfect for occasional use'
			],
			highlight: false,
			isFree: false,
			productId: 'topup',
			isTopup: true
		}
	];

	const handleSelectPlan = async (planName: string, productIdOverride?: string | null, isTopup?: boolean) => {
		if (!user) {
			navigate('/login', { state: { from: '/pricing', createAccount: true } });
			return;
		}
		const productId = productIdOverride !== undefined ? productIdOverride : PLAN_TO_PRODUCT_ID[planName];
		if (productId === null) {
			toast.info('You’re on the free plan. Create an account to get 15 credits.');
			return;
		}
		const userId = getUserId();
		const authHeader = getAuthHeader() as Record<string, string> | undefined;
		if (!userId || !authHeader?.Authorization) {
			toast.error('Session expired. Please sign in again.');
			return;
		}
		setLoadingPlan(planName);
		try {
			const { url } = await PaymentService.createCheckout(
				userId,
				isTopup 
					? { productId: 'topup', quantity: creditPackQuantity }
					: (() => {
						if (productId !== 'starter' && productId !== 'pro' && productId !== 'byok') {
							throw new Error('Invalid product ID');
						}
						return { productId: productId as 'starter' | 'pro' | 'byok' };
					})(),
				authHeader
			);
			if (url) {
				window.location.href = url;
			} else {
				toast.error('Could not start checkout.');
				setLoadingPlan(null);
			}
		} catch (e: any) {
			const msg = e?.message || '';
			if (msg.includes('Payments not configured') || msg.includes('not configured')) {
				toast.error('Payments are not available at the moment. Please try again later or contact support.');
			} else {
				toast.error(msg || 'Checkout failed. Try again.');
			}
			setLoadingPlan(null);
		}
	};

	const handleManageSubscription = async () => {
		const userId = getUserId();
		const authHeader = getAuthHeader() as Record<string, string> | undefined;
		if (!userId || !authHeader?.Authorization) return;
		setPortalLoading(true);
		try {
			const { url } = await PaymentService.getPortalUrl(userId, authHeader);
			if (url) window.location.href = url;
		} catch (e: any) {
			toast.error(e?.message || 'Could not open subscription management.');
		} finally {
			setPortalLoading(false);
		}
	};

	return (
		<>
			<ToastContainer position="top-right" />
			<Box
				sx={{
					py: { xs: 6, md: 8 },
					textAlign: 'center',
					color: 'common.white',
					background: 'linear-gradient(135deg, #FF9800 0%, #FF6F00 50%, #E65100 100%)'
				}}
			>
				<Container maxWidth="lg">
					<motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
						<Typography variant="h2" fontWeight={800} sx={{ mb: 2, textTransform: 'uppercase' }}>
							Pricing Plans
						</Typography>
						<Typography variant="h6" sx={{ maxWidth: 800, mx: 'auto', opacity: 0.95, mb: 2 }}>
							Choose the plan that works best for you. Our pricing is designed to keep our service running smoothly while providing you with a scalable, secure, and performant platform.
						</Typography>
						<Paper
							sx={{
								display: 'inline-block',
								px: 3,
								py: 1.5,
								borderRadius: 2,
								backgroundColor: 'rgba(255, 255, 255, 0.2)',
								backdropFilter: 'blur(10px)'
							}}
						>
							<Typography variant="h6" fontWeight={700}>
								🎉 Get 15 free credits to start!
							</Typography>
							<Typography variant="body2" sx={{ mt: 0.5 }}>
								Every new account receives 15 AI credits to try out our tool
							</Typography>
						</Paper>
					</motion.div>
				</Container>
			</Box>

			<Container maxWidth="lg" sx={{ py: 8 }}>
				{user && (subscriptionLoading || subscription?.hasSubscription) && (
					<Paper
						sx={{
							mb: 4,
							p: 2,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							flexWrap: 'wrap',
							gap: 2,
							background: (theme) =>
								theme.palette.mode === 'dark'
									? 'linear-gradient(135deg, rgba(25, 118, 210, 0.15) 0%, rgba(25, 118, 210, 0.05) 100%)'
									: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.02) 100%)',
							border: '1px solid',
							borderColor: 'primary.main'
						}}
					>
						{subscriptionLoading ? (
							<Stack direction="row" spacing={2} alignItems="center">
								<CircularProgress size={24} />
								<Typography color="text.secondary">Loading subscription…</Typography>
							</Stack>
						) : subscription?.hasSubscription ? (
							<>
								<Typography variant="h6" fontWeight={600}>
									{t.dashboard.youAreOnPlan.replace(
										'{plan}',
										subscription.planName || 'Subscription'
									)}
								</Typography>
								<Button
									variant="outlined"
									onClick={handleManageSubscription}
									disabled={portalLoading}
								>
									{portalLoading ? <CircularProgress size={20} /> : t.dashboard.manageSubscription}
								</Button>
							</>
						) : null}
					</Paper>
				)}
				<Grid container spacing={4} justifyContent="center" alignItems="stretch">
					{plans.filter(p => !p.isTopup).map((plan, index) => (
						<Grid item xs={12} sm={6} md={3} key={plan.name}>
							<motion.div
								initial={{ opacity: 0, y: 30 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: index * 0.1 }}
							>
								<Card
									sx={{
										height: '100%',
										display: 'flex',
										flexDirection: 'column',
										position: 'relative',
										border: plan.highlight ? 3 : 1,
										borderColor: plan.highlight ? 'secondary.main' : 'divider',
										boxShadow: plan.highlight ? 6 : 2,
										'&:hover': {
											boxShadow: 8,
											transform: 'translateY(-4px)',
											transition: 'all 0.3s ease-in-out'
										}
									}}
								>
									{plan.highlight && (
										<Box
											sx={{
												position: 'absolute',
												top: 16,
												right: 16,
												backgroundColor: 'secondary.main',
												color: 'common.white',
												px: 1.5,
												py: 0.5,
												borderRadius: 1,
												fontSize: '0.75rem',
												fontWeight: 700
											}}
										>
											POPULAR
										</Box>
									)}
									<CardContent sx={{ flexGrow: 1, pt: plan.highlight ? 4 : 3 }}>
										<Stack spacing={2} alignItems="center" sx={{ mb: 3 }}>
											<Box
												sx={{
													color: `${plan.color}.main`,
													mb: 1
												}}
											>
												{plan.icon}
											</Box>
											<Typography variant="h5" fontWeight={800}>
												{plan.name}
											</Typography>
											<Typography variant="body2" color="text.secondary" textAlign="center">
												{plan.description}
											</Typography>
										</Stack>

										<Divider sx={{ my: 2 }} />

										<Stack spacing={1} alignItems="center" sx={{ mb: 3 }}>
											<Stack direction="row" alignItems="baseline" spacing={0.5}>
												<Typography variant="h3" fontWeight={800} color={`${plan.color}.main`}>
													{plan.price}
												</Typography>
												{plan.period && (
													<Typography variant="body1" color="text.secondary">
														/{plan.period}
													</Typography>
												)}
											</Stack>
											<Typography variant="h6" fontWeight={600} color="text.primary">
												{plan.credits}
											</Typography>
										</Stack>

										<Divider sx={{ my: 2 }} />

										<Stack spacing={1.5}>
											{plan.features.map((feature, idx) => (
												<Stack key={idx} direction="row" spacing={1.5} alignItems="flex-start">
													<CheckCircleIcon
														sx={{
															color: `${plan.color}.main`,
															fontSize: 20,
															mt: 0.25,
															flexShrink: 0
														}}
													/>
													<Typography variant="body2" color="text.secondary">
														{feature}
													</Typography>
												</Stack>
											))}
										</Stack>
									</CardContent>
									<CardActions sx={{ p: 3, pt: 0 }}>
										<Button
											fullWidth
											variant={plan.highlight ? 'contained' : 'outlined'}
											color={(plan.color === 'info' ? 'info' : plan.color) as 'primary' | 'secondary' | 'success' | 'info'}
											size="large"
											onClick={() => handleSelectPlan(plan.name, plan.productId, plan.isTopup)}
											disabled={loadingPlan !== null}
										>
											{loadingPlan === plan.name ? (
												<Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
													<CircularProgress color="inherit" size={20} />
													<span>Redirecting…</span>
												</Stack>
											) : user ? (
												plan.isTopup ? 'Buy Credits' : 'Select Plan'
											) : (
												'Create Account'
											)}
										</Button>
									</CardActions>
								</Card>
							</motion.div>
						</Grid>
					))}
					{plans.filter(p => p.isTopup).map((plan, index) => {
						const totalCredits = creditPackQuantity * CREDITS_PER_PACK;
						const totalPrice = creditPackQuantity * PRICE_PER_PACK;
						return (
							<Grid item xs={12} key={plan.name}>
								<motion.div
									initial={{ opacity: 0, y: 30 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ duration: 0.5, delay: (plans.filter(p => !p.isTopup).length + index) * 0.1 }}
								>
									<Card
										sx={{
											display: 'flex',
											flexDirection: { xs: 'column', md: 'row' },
											position: 'relative',
											border: 1,
											borderColor: 'divider',
											boxShadow: 2,
											'&:hover': {
												boxShadow: 4,
												transform: 'translateY(-2px)',
												transition: 'all 0.3s ease-in-out'
											}
										}}
									>
										<CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: 3, p: 3 }}>
											<Box sx={{ color: `${plan.color}.main` }}>
												{plan.icon}
											</Box>
											<Box sx={{ flexGrow: 1 }}>
												<Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
													{plan.name}
												</Typography>
												<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
													{plan.description}
												</Typography>
												<Stack spacing={3}>
													<Box>
														<Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
															${PRICE_PER_PACK} per {CREDITS_PER_PACK} credits
														</Typography>
													</Box>
													<Box>
														<Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
															<Typography variant="body2" fontWeight={600}>
																Quantity:
															</Typography>
															<Stack direction="row" spacing={1} alignItems="center">
																<IconButton
																	size="small"
																	onClick={() => setCreditPackQuantity(Math.max(1, creditPackQuantity - 1))}
																	disabled={creditPackQuantity <= 1}
																	sx={{
																		border: 1,
																		borderColor: 'divider',
																		'&:hover': {
																			backgroundColor: 'action.hover'
																		}
																	}}
																>
																	<RemoveIcon />
																</IconButton>
																<TextField
																	type="number"
																	value={creditPackQuantity}
																	onChange={(e) => {
																		const val = parseInt(e.target.value, 10);
																		if (!isNaN(val) && val >= 1 && val <= MAX_PACKS) {
																			setCreditPackQuantity(val);
																		}
																	}}
																	onBlur={(e) => {
																		const val = parseInt(e.target.value, 10);
																		if (isNaN(val) || val < 1) {
																			setCreditPackQuantity(1);
																		} else if (val > MAX_PACKS) {
																			setCreditPackQuantity(MAX_PACKS);
																		}
																	}}
																	inputProps={{ min: 1, max: MAX_PACKS, style: { textAlign: 'center' } }}
																	sx={{ width: 80 }}
																	size="small"
																/>
																<IconButton
																	size="small"
																	onClick={() => setCreditPackQuantity(Math.min(MAX_PACKS, creditPackQuantity + 1))}
																	disabled={creditPackQuantity >= MAX_PACKS}
																	sx={{
																		border: 1,
																		borderColor: 'divider',
																		'&:hover': {
																			backgroundColor: 'action.hover'
																		}
																	}}
																>
																	<AddIcon />
																</IconButton>
																<Typography variant="body2" color="text.secondary">
																	pack{creditPackQuantity !== 1 ? 's' : ''}
																</Typography>
															</Stack>
														</Stack>
														<Box sx={{ px: 1 }}>
															<Slider
																value={creditPackQuantity}
																onChange={(_, newValue) => {
																	if (typeof newValue === 'number') {
																		setCreditPackQuantity(newValue);
																	}
																}}
																min={1}
																max={MAX_PACKS}
																step={1}
																marks={[
																	{ value: 1, label: '1' },
																	{ value: 5, label: '5' },
																	{ value: 10, label: '10' },
																	{ value: 15, label: '15' },
																	{ value: 20, label: '20' }
																]}
																valueLabelDisplay="auto"
																valueLabelFormat={(value) => `${value} pack${value !== 1 ? 's' : ''}`}
																sx={{
																	color: `${plan.color}.main`,
																	'& .MuiSlider-thumb': {
																		width: 20,
																		height: 20,
																		'&:hover': {
																			boxShadow: `0 0 0 8px rgba(${plan.color === 'warning' ? '255, 152, 0' : plan.color === 'primary' ? '25, 118, 210' : '0, 0, 0'}, 0.16)`
																		}
																	},
																	'& .MuiSlider-track': {
																		height: 6
																	},
																	'& .MuiSlider-rail': {
																		height: 6,
																		opacity: 0.3
																	},
																	'& .MuiSlider-markLabel': {
																		fontSize: '0.75rem',
																		fontWeight: 600
																	}
																}}
															/>
														</Box>
													</Box>
													<Divider />
													<Paper
														sx={{
															p: 2,
															backgroundColor: `${plan.color}.light`,
															backgroundImage: `linear-gradient(135deg, ${plan.color === 'warning' ? 'rgba(255, 152, 0, 0.1)' : plan.color === 'primary' ? 'rgba(25, 118, 0, 0.1)' : 'rgba(0, 0, 0, 0.05)'} 0%, transparent 100%)`,
															borderRadius: 2
														}}
													>
														<Stack direction="row" spacing={2} alignItems="baseline" justifyContent="space-between">
															<Box>
																<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
																	Total Price
																</Typography>
																<Typography variant="h4" fontWeight={800} color={`${plan.color}.main`}>
																	${totalPrice.toFixed(2)}
																</Typography>
															</Box>
															<Box sx={{ textAlign: 'right' }}>
																<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
																	Total Credits
																</Typography>
																<Typography variant="h4" fontWeight={800} color={`${plan.color}.main`}>
																	{totalCredits}
																</Typography>
															</Box>
														</Stack>
													</Paper>
												</Stack>
											</Box>
											<CardActions sx={{ p: 0, alignSelf: { xs: 'stretch', md: 'center' } }}>
												<Button
													variant="contained"
													color={plan.color as 'primary' | 'secondary' | 'success' | 'warning' | 'info'}
													size="large"
													onClick={() => handleSelectPlan(plan.name, plan.productId, plan.isTopup)}
													disabled={loadingPlan !== null}
													sx={{ minWidth: 150, width: { xs: '100%', md: 'auto' } }}
												>
													{loadingPlan === plan.name ? (
														<Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
															<CircularProgress color="inherit" size={20} />
															<span>Redirecting…</span>
														</Stack>
													) : user ? (
														`Buy ${totalCredits} Credits`
													) : (
														'Create Account'
													)}
												</Button>
											</CardActions>
										</CardContent>
									</Card>
								</motion.div>
							</Grid>
						);
					})}
				</Grid>
			</Container>

			<Container maxWidth="md" sx={{ pb: 8 }}>
				<Paper
					sx={{
						p: { xs: 4, md: 6 },
						borderRadius: 4,
						background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 111, 0, 0.1) 100%)',
						border: '1px solid',
						borderColor: 'divider'
					}}
				>
					<Stack spacing={3} alignItems="center" textAlign="center">
						<Typography variant="h5" fontWeight={800}>
							Why Our Pricing?
						</Typography>
						<Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600 }}>
							Our pricing structure is designed to keep our service running smoothly. These prices help us maintain a scalable, secure, and performant platform that you can rely on for your home brewing journey.
						</Typography>
						<Grid container spacing={3} sx={{ mt: 2 }}>
							<Grid item xs={12} sm={4}>
								<Stack spacing={1} alignItems="center" textAlign="center">
									<SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />
									<Typography variant="subtitle1" fontWeight={700}>
										Secure
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Enterprise-grade security to protect your data
									</Typography>
								</Stack>
							</Grid>
							<Grid item xs={12} sm={4}>
								<Stack spacing={1} alignItems="center" textAlign="center">
									<ScaleIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
									<Typography variant="subtitle1" fontWeight={700}>
										Scalable
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Infrastructure that grows with your needs
									</Typography>
								</Stack>
							</Grid>
							<Grid item xs={12} sm={4}>
								<Stack spacing={1} alignItems="center" textAlign="center">
									<SpeedIcon sx={{ fontSize: 40, color: 'success.main' }} />
									<Typography variant="subtitle1" fontWeight={700}>
										Performant
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Fast and reliable service when you need it
									</Typography>
								</Stack>
							</Grid>
						</Grid>
					</Stack>
				</Paper>
			</Container>

			<Container maxWidth="md" sx={{ pb: 8, textAlign: 'center' }}>
				<Typography variant="body2" color="text.secondary">
					<strong>Note:</strong> Each AI credit equals one AI request. New accounts receive 15 free credits to get started. Credits reset monthly. Unused credits do not roll over.
				</Typography>
			</Container>
		</>
	);
};

export default PricingPage;
